/**
 * Twee-staps-verificatie aan- en uitzetten voor het eigen beheerdersaccount.
 *
 * Deze sectie stond eerder op de Instellingen-pagina en is op 2026-05-31 per ongeluk
 * verdwenen toen die pagina werd herschreven (commit 5239ec7). De backend-eindpunten
 * bleven bestaan, het inlogscherm bleef om een code vragen, en op het gebruikersoverzicht
 * bleef "MFA: Aan/Uit" staan — maar er was bijna drie maanden lang geen enkele plek meer
 * om hem áán te zetten. De vijf rode tests waren het alarm; dat is op 2026-08-20 opgemerkt
 * en hier hersteld.
 *
 * De weg:
 *   1. GET  /api/auth/admin/mfa/status              → welke factoren staan er
 *   2. POST /api/auth/admin/mfa/enroll              → QR-code + secret
 *   3. POST /api/auth/admin/mfa/verify-enrollment   → code bevestigen, MFA staat aan
 *   4. DELETE /api/auth/admin/mfa/factor/{id}       → uitzetten, met wachtwoord erbij
 */
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminGet, adminApiInstance } from "../../lib/adminApi";
import { ErrorBanner } from "./AdminUI";

/** De server (`MFADisableRequest`) eist dit minimum; hier staat het ook, zodat de
 *  beheerder geen Engelse validatiefout uit de code te zien krijgt. */
const MINIMALE_WACHTWOORDLENGTE = 12;

const KAART = {
  borderRadius: "var(--radius-vlak)", border: "1px solid var(--border-subtle)",
  background: "var(--surface-card)", boxShadow: "var(--elevation-card)",
};
const KNOP = {
  minHeight: 44, borderRadius: "var(--radius)", padding: "var(--ruimte-4) var(--ruimte-6)",
  font: "var(--text-label)", cursor: "pointer", border: "none",
};

function Merkje({ actief }) {
  const stijl = {
    display: "inline-flex", alignItems: "center", gap: "var(--ruimte-3)",
    borderRadius: "var(--radius-pil)", padding: "2px var(--ruimte-4)",
    fontSize: "var(--fs-xs)", fontWeight: "var(--fw-medium)",
  };
  return actief ? (
    <span data-testid="mfa-status-badge-active"
      style={{ ...stijl, background: "var(--positief-zacht)", color: "var(--text-positief)" }}>
      Tweestapsverificatie staat aan
    </span>
  ) : (
    <span data-testid="mfa-status-badge-inactive"
      style={{ ...stijl, background: "var(--aandacht-zacht)", color: "var(--text-aandacht)" }}>
      Tweestapsverificatie staat uit
    </span>
  );
}

function Aanzetten({ onKlaar }) {
  const [fase, setFase] = useState("start"); // start | code | klaar
  const [gegevens, setGegevens] = useState(null);
  const [code, setCode] = useState("");
  const [fout, setFout] = useState(null);

  const startMut = useMutation({
    mutationFn: () => adminApiInstance().post("/api/auth/admin/mfa/enroll").then((r) => r.data),
    onSuccess: (data) => { setGegevens(data); setFase("code"); setFout(null); },
    onError: setFout,
  });
  const bevestigMut = useMutation({
    mutationFn: () => adminApiInstance()
      .post("/api/auth/admin/mfa/verify-enrollment", { factor_id: gegevens.factor_id, code })
      .then((r) => r.data),
    onSuccess: () => { setFase("klaar"); toast.success("Tweestapsverificatie staat aan"); onKlaar?.(); },
    onError: setFout,
  });

  function kopieerSleutel() {
    if (!gegevens?.secret_base32) return;
    navigator.clipboard?.writeText(gegevens.secret_base32);
    toast.success("Sleutel gekopieerd");
  }

  if (fase === "start") {
    return (
      <div data-testid="mfa-enroll-panel">
        <p style={{ fontSize: "var(--fs-body)", color: "var(--text-body)", marginBottom: "var(--ruimte-5)" }}>
          Met tweestapsverificatie vraagt het inlogscherm naast uw wachtwoord om een code
          uit een app op uw telefoon (bijvoorbeeld Google Authenticator, 1Password of Authy).
          Iemand met alleen uw wachtwoord komt er dan niet in.
        </p>
        <button type="button" data-testid="btn-mfa-start-enroll"
          onClick={() => startMut.mutate()} disabled={startMut.isPending}
          style={{ ...KNOP, background: "var(--ink)", color: "var(--text-inverse)",
                   opacity: startMut.isPending ? 0.6 : 1 }}>
          {startMut.isPending ? "Bezig…" : "Tweestapsverificatie aanzetten"}
        </button>
        <ErrorBanner error={fout} testid="mfa-enroll-error" />
      </div>
    );
  }

  if (fase === "code") {
    return (
      <div data-testid="mfa-enroll-qr-panel" style={{ display: "grid", gap: "var(--ruimte-6)" }}>
        <ol style={{ paddingLeft: "var(--ruimte-6)", fontSize: "var(--fs-body)", color: "var(--text-body)",
                     listStyle: "decimal", display: "grid", gap: "var(--ruimte-2)" }}>
          <li>Open de app op uw telefoon en scan de code hieronder.</li>
          <li>Lukt scannen niet, tik dan de sleutel met de hand over.</li>
          <li>Vul de zes cijfers uit de app in om te bevestigen.</li>
        </ol>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--ruimte-7)", alignItems: "flex-start" }}>
          <img src={gegevens.qr_code_data_url} alt="QR-code voor uw authenticator-app"
            data-testid="mfa-qr-image" width="200" height="200"
            style={{ borderRadius: "var(--radius)", border: "1px solid var(--border-subtle)",
                     background: "#fff", padding: "var(--ruimte-3)" }} />
          <div style={{ display: "grid", gap: "var(--ruimte-5)" }}>
            <div>
              <div style={{ font: "var(--text-label)", fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
                Sleutel om over te tikken
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--ruimte-4)", marginTop: "var(--ruimte-2)" }}>
                <code data-testid="mfa-secret"
                  style={{ borderRadius: "var(--radius-klein)", background: "var(--neutraal-100)",
                           padding: "var(--ruimte-3) var(--ruimte-5)", fontSize: "var(--fs-xs)",
                           fontFamily: "monospace", wordBreak: "break-all" }}>
                  {gegevens.secret_base32}
                </code>
                <button type="button" data-testid="btn-mfa-copy-secret" onClick={kopieerSleutel}
                  style={{ background: "none", border: "none", cursor: "pointer",
                           fontSize: "var(--fs-xs)", color: "var(--accent)", textDecoration: "underline" }}>
                  Kopieer
                </button>
              </div>
            </div>
            <label style={{ display: "block" }}>
              <span style={{ font: "var(--text-label)", fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
                Zes cijfers uit de app
              </span>
              <input data-testid="mfa-code-input" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                style={{ marginTop: "var(--ruimte-2)", display: "block", width: 140, minHeight: 44,
                         borderRadius: "var(--radius)", border: "1px solid var(--border-strong)",
                         padding: "var(--ruimte-4) var(--ruimte-5)", fontFamily: "monospace",
                         // --fs-invoer-mobiel is de harde ondergrens van 16px: daaronder zoomt iOS in,
                         // en dit veld tikt men bij uitstek op een telefoon.
                         fontSize: "var(--fs-invoer-mobiel)", letterSpacing: "0.15em" }} />
            </label>
            <button type="button" data-testid="btn-mfa-verify"
              onClick={() => bevestigMut.mutate()}
              disabled={code.length !== 6 || bevestigMut.isPending}
              style={{ ...KNOP, background: "var(--actie)", color: "var(--text-inverse)",
                       justifySelf: "start", opacity: code.length !== 6 || bevestigMut.isPending ? 0.5 : 1 }}>
              {bevestigMut.isPending ? "Controleren…" : "Bevestigen"}
            </button>
          </div>
        </div>
        <ErrorBanner error={fout} testid="mfa-enroll-error" />
      </div>
    );
  }

  return (
    <div data-testid="mfa-enroll-done-panel"
      style={{ borderRadius: "var(--radius)", background: "var(--positief-zacht)",
               border: "1px solid var(--positief-rand)", padding: "var(--ruimte-6)" }}>
      <p style={{ font: "var(--text-label)", color: "var(--text-positief)" }}>
        Tweestapsverificatie staat aan voor uw account.
      </p>
      <p style={{ marginTop: "var(--ruimte-2)", fontSize: "var(--fs-sm)", color: "var(--text-positief)" }}>
        Bij de volgende keer inloggen vraagt het scherm om een code uit de app.
      </p>
    </div>
  );
}

function Uitzetten({ factor, onUit }) {
  const [open, setOpen] = useState(false);
  const [wachtwoord, setWachtwoord] = useState("");
  const [fout, setFout] = useState(null);

  const uitMut = useMutation({
    mutationFn: () => adminApiInstance().delete(
      `/api/auth/admin/mfa/factor/${factor.factor_id}`,
      { data: { password_confirmation: wachtwoord } },
    ),
    onSuccess: () => {
      toast.success("Tweestapsverificatie staat uit");
      setOpen(false); setWachtwoord(""); onUit?.();
    },
    onError: setFout,
  });

  return (
    <>
      <button type="button" data-testid={`btn-mfa-disable-${factor.factor_id}`}
        onClick={() => setOpen(true)}
        style={{ background: "none", border: "none", cursor: "pointer",
                 fontSize: "var(--fs-xs)", color: "var(--text-alarm)", textDecoration: "underline" }}>
        Verwijderen
      </button>
      {open ? (
        <div data-testid="mfa-disable-modal" role="dialog" aria-modal="true"
          style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex",
                   alignItems: "center", justifyContent: "center",
                   background: "rgba(11,32,39,0.4)", padding: "var(--ruimte-6)" }}>
          <div style={{ ...KAART, width: "100%", maxWidth: 380, padding: "var(--ruimte-7)",
                        boxShadow: "var(--elevation-raised)" }}>
            <h3 style={{ font: "var(--text-h3)", color: "var(--text-strong)" }}>
              Tweestapsverificatie uitzetten
            </h3>
            <p style={{ marginTop: "var(--ruimte-3)", fontSize: "var(--fs-body)", color: "var(--text-body)" }}>
              Uw account is daarna alleen nog met een wachtwoord beschermd. Bevestig met uw
              huidige wachtwoord.
            </p>
            <label style={{ display: "block", marginTop: "var(--ruimte-5)" }}>
              <span style={{ font: "var(--text-label)", fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
                Wachtwoord
              </span>
              <input type="password" data-testid="mfa-disable-password" value={wachtwoord}
                onChange={(e) => setWachtwoord(e.target.value)}
                style={{ marginTop: "var(--ruimte-2)", display: "block", width: "100%", minHeight: 44,
                         borderRadius: "var(--radius)", border: "1px solid var(--border-strong)",
                         padding: "var(--ruimte-4) var(--ruimte-5)", fontSize: "var(--fs-body)" }} />
            </label>
            {/* De server eist minstens twaalf tekens. Zonder deze regel kreeg de
                beheerder daarvoor een Engelse 422 met een veldnaam uit de code, terwijl
                het echte antwoord "wachtwoord onjuist" hoort te zijn. */}
            <p style={{ marginTop: "var(--ruimte-2)", fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
              Minstens {MINIMALE_WACHTWOORDLENGTE} tekens.
            </p>
            <ErrorBanner error={fout} testid="mfa-disable-error" />
            <div style={{ marginTop: "var(--ruimte-6)", display: "flex", justifyContent: "flex-end", gap: "var(--ruimte-4)" }}>
              <button type="button" data-testid="btn-mfa-disable-cancel"
                onClick={() => { setOpen(false); setFout(null); }}
                style={{ ...KNOP, background: "var(--surface-card)", color: "var(--text-body)",
                         border: "1px solid var(--border-strong)" }}>
                Annuleren
              </button>
              <button type="button" data-testid="btn-mfa-disable-confirm"
                onClick={() => uitMut.mutate()}
                disabled={wachtwoord.length < MINIMALE_WACHTWOORDLENGTE || uitMut.isPending}
                style={{ ...KNOP, background: "var(--alarm)", color: "var(--text-inverse)",
                         opacity: wachtwoord.length < MINIMALE_WACHTWOORDLENGTE || uitMut.isPending ? 0.5 : 1 }}>
                {uitMut.isPending ? "Bezig…" : "Uitzetten"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export function MfaSectie() {
  const qc = useQueryClient();
  const statusQ = useQuery({
    queryKey: ["mfa-status"],
    queryFn: () => adminGet("/api/auth/admin/mfa/status"),
  });
  const actief = Boolean(statusQ.data?.enrolled);
  const alleFactoren = statusQ.data?.factors || [];
  // Een afgebroken poging laat een onbevestigde factor achter (de server maakt er bij
  // elke poging een nieuwe aan). Die kan geen code geven en hoort dus niet in een lijst
  // die zegt dat dat wél kan.
  const bevestigd = alleFactoren.filter((f) => f.status === "verified");
  // Maar: de server bepaalt `enrolled` niet op `status` alleen. Valt dat een keer anders
  // uit, dan zou een lege lijst betekenen dat de beheerder MFA niet meer úit kan zetten.
  // Dan liever de ruwe lijst tonen dan een uitweg afsluiten.
  const factoren = bevestigd.length ? bevestigd : alleFactoren;

  function ververs() {
    qc.invalidateQueries({ queryKey: ["mfa-status"] });
  }

  return (
    <section data-testid="instellingen-mfa-sectie" style={KAART} aria-labelledby="mfa-kop">
      <header style={{ borderBottom: "1px solid var(--border-subtle)", background: "var(--surface-sunken)",
                       padding: "var(--ruimte-5) var(--ruimte-6)", display: "flex",
                       flexWrap: "wrap", alignItems: "center", justifyContent: "space-between",
                       gap: "var(--ruimte-4)", borderTopLeftRadius: "var(--radius-vlak)",
                       borderTopRightRadius: "var(--radius-vlak)" }}>
        <div>
          <h2 id="mfa-kop" style={{ font: "var(--text-label)", color: "var(--text-strong)" }}>
            Beveiliging van uw account
          </h2>
          <p style={{ marginTop: 2, fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
            Tweestapsverificatie met een code uit een app op uw telefoon
          </p>
        </div>
        <Merkje actief={actief} />
      </header>

      <div style={{ padding: "var(--ruimte-6)" }}>
        <ErrorBanner error={statusQ.error} testid="mfa-status-error" />
        {statusQ.isLoading ? (
          <p data-testid="mfa-loading" style={{ fontSize: "var(--fs-body)", color: "var(--text-soft)" }}>Laden…</p>
        ) : actief ? (
          <div data-testid="mfa-active-panel" style={{ display: "grid", gap: "var(--ruimte-5)" }}>
            <p style={{ fontSize: "var(--fs-body)", color: "var(--text-body)" }}>
              Uw account is beschermd. Hieronder staan de apparaten die een code kunnen geven.
            </p>
            <ul style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)", listStyle: "none", margin: 0, padding: 0 }}>
              {factoren.map((f, i) => (
                <li key={f.factor_id} data-testid={`mfa-factor-${f.factor_id}`}
                  style={{ display: "flex", flexWrap: "wrap", alignItems: "center",
                           justifyContent: "space-between", gap: "var(--ruimte-4)",
                           padding: "var(--ruimte-5) var(--ruimte-6)",
                           borderTop: i === 0 ? "none" : "1px solid var(--border-subtle)" }}>
                  <div>
                    <div style={{ font: "var(--text-label)", color: "var(--text-strong)" }}>
                      {f.friendly_name || "Authenticator-app"}
                    </div>
                    <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
                      {f.factor_type || "totp"}
                    </div>
                  </div>
                  <Uitzetten factor={f} onUit={ververs} />
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <Aanzetten onKlaar={ververs} />
        )}
      </div>
    </section>
  );
}
